import type { SeriesConfig } from '@/pages/dashboard/components/EventMetricTrend/types';
import type { IKpiDimensionReqData } from './_type';
import { getName } from '@home/utils/common';
import type { INormalizedMetricsTrend } from '../KpiEventMetricTrendService/_type';
import {
  formatYmdToIsoDate,
  kpiMetricsTrendBeforeRequest,
  resolveLastWeekDateForTrendRow,
  shapeTrendSeriesRowLikeSpec,
} from '../KpiEventMetricTrendService/_utils';
import { getCascadeChildValues } from '../KpiApplovinRegionTrendService/applovinRegionTrendUtils';

type TDimensionMetricCell = {
  value?: string;
  last_value?: string;
  sdlw_value?: string;
};

type TDimensionRow = {
  date: string;
  /** 指标单元格可能为 null（接口占位行） */
  metrics?: Record<string, TDimensionMetricCell | null> | null;
  /** 与 date 平级、按 key 取维度值（如 ua_network 走 custom_dimensions） */
  custom_dimensions?: Array<{ key?: string; value?: string; }>;
} & Record<string, unknown>;

/**
 * 解析 dimension/metrics_trend 行列表。
 * http 拦截器多数返回裸数组；若仍传入 { code, data } / { list } / { records } 需兼容，否则会 rows=[] → 无折线、仅事件层有内容。
 */
const extractDimensionTrendRows = (raw: unknown): TDimensionRow[] => {
  if (raw === null || raw === undefined) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw as TDimensionRow[];
  }
  if (typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  /** overall_performance 等：{ code, data: { metrics_list } } 或 { metrics_list } */
  if (o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
    const dataObj = o.data as Record<string, unknown>;
    if (Array.isArray(dataObj.metrics_list)) {
      return dataObj.metrics_list as TDimensionRow[];
    }
  }
  if (Array.isArray(o.metrics_list)) {
    return o.metrics_list as TDimensionRow[];
  }
  if (Array.isArray(o.data)) {
    return o.data as TDimensionRow[];
  }
  if (Array.isArray(o.list)) {
    return o.list as TDimensionRow[];
  }
  if (Array.isArray(o.records)) {
    return o.records as TDimensionRow[];
  }
  const nested = o.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as Record<string, unknown>;
    if (Array.isArray(inner.list)) {
      return inner.list as TDimensionRow[];
    }
    if (Array.isArray(inner.data)) {
      return inner.data as TDimensionRow[];
    }
  }
  return [];
};

/**
 * 维度趋势：空/缺失视为无数据（null），折线在 ECharts 中断开；显式数值含 0 仍按数字解析。
 */
const parseOptionalMetricValue = (raw: string | undefined): number | null => {
  if (raw === undefined || raw === null) {
    return null;
  }
  const s = String(raw).trim();
  if (s === '') {
    return null;
  }
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
};

const cellToOptionalNumber = (
  cell: TDimensionMetricCell | null | undefined,
): number | null => {
  if (!cell || typeof cell !== 'object') {
    return null;
  }
  return parseOptionalMetricValue(cell.value);
};

const normalizeDateKey = (raw: string): string => {
  if (raw && raw.length === 8 && /^\d{8}$/.test(raw)) {
    return formatYmdToIsoDate(raw);
  }
  return String(raw);
};

/**
 * - custom_dimensions：与 metrics_trend 一致，从 custom_dimensions 中按 dimensionKey 取 value
 * - row_field：与 date 平级字段作维度 code（如 system），用于 accumulation.by_system 等返回结构
 */
const extractDimValue = (
  row: TDimensionRow,
  dimensionKey: string,
  source: 'custom_dimensions' | 'row_field' = 'custom_dimensions',
  rowField?: string,
  secondaryRowField?: string,
): string => {
  if (source === 'row_field') {
    const field = (rowField ?? dimensionKey ?? 'system').trim() || 'system';
    const v = row[field];
    if (v === undefined || v === null || String(v).trim() === '') {
      return 'unknown';
    }
    let primary = String(v).trim();
    const secKey = secondaryRowField?.trim();
    if (secKey) {
      const sv = row[secKey];
      if (sv !== undefined && sv !== null && String(sv).trim() !== '') {
        primary = `${primary}_${String(sv).trim()}`;
      }
    }
    return primary;
  }
  const arr = row.custom_dimensions;
  if (!Array.isArray(arr)) {
    return 'unknown';
  }
  const hit = arr.find(x => x?.key === dimensionKey);
  const v = hit?.value;
  if (v === undefined || v === null || String(v).trim() === '') {
    return 'unknown';
  }
  return String(v).trim();
};

/** 占位名：与接口原始维度值一致；有 selector 映射时在 Inner 内由 getDimensionLegendEnCn 覆盖 */
const toLegendNames = (raw: string): { cnName: string; enName: string; } => {
  const t = String(raw || 'unknown').trim() || 'unknown';
  return { cnName: t, enName: t };
};

const DIMENSION_LINE_COLORS = [
  '#5086F3',
  '#69D19C',
  '#6E7794',
  '#F5D242',
  '#7F6FF1',
  '#EC5656',
  '#00A870',
  '#0052D9',
  '#ED7B2F',
  '#D54941',
];

export type TNormalizeDimensionOptions = {
  dimensionKey?: string;
  /**
   * 维度取值来源：默认 custom_dimensions；row_field 时从行字段读 code（如 system），与 selectorMapKey 配合做映射
   */
  dimensionValueSource?: 'custom_dimensions' | 'row_field';
  /** row_field 时读取的字段名，缺省用 dimensionKey（建议在配置中显式写 system 等） */
  dimensionRowField?: string;
  /**
   * row_field 时与主维度字段拼接成唯一维度键：`主值_次值`（如 `1_us`），用于 by_country + by_system 同时拆线；
   * 次字段缺省或为空时仅使用主字段（与仅按 system 拆线一致）。
   */
  dimensionSecondaryRowField?: string;
  /** 图例条数上限，与 top_num 对齐 */
  maxSeries?: number;
  /** 折线点值格式化，与 chartConfig 一致 */
  valueFormatRule?: string;
  /**
   * 响应体 metrics 对象中的字段名；若与请求 metrics[0] 不一致须配置（如请求 d7、返回 total_users_d7）
   */
  metricResponseKey?: string;
  /**
   * 在按指标加总降序取 Top N 之后，将列表中**已出现**的维度原始值按本数组顺序挪到 `dimensionSeriesConfig` 最前
   *（与 `dim_v_0` 及 ECharts 首条 series 对齐）。每个 token 匹配全等或 `token_*` 前缀（如 `1` 匹配 `1`、`1_us`）。
   */
  dimensionSeriesOrderPriority?: string[];
  /**
   * 维度原始值（与 `dimRawCode` 一致）→ 折线/图例颜色（如 `#5086F3`）。未命中则仍用默认色板按序取色。
   */
  dimensionSeriesColorMap?: Record<string, string>;
};

export type TDimensionNormalizedTrend = INormalizedMetricsTrend & {
  /** 由接口维度值生成，供子组件覆盖静态 seriesConfig */
  dimensionSeriesConfig: SeriesConfig[];
  /** 与 normalize 时 valueFormatRule 一致，供下载列数字格式 */
  dimensionValueFormatRule: string;
};

/** chartConfig.formatRule / series 数值格式 → Excel 列 format */
const DIMENSION_VALUE_FORMAT_RULE_TO_EXCEL: Record<string, string> = {
  thousands2Decimal: '#,##0.00',
  thousandsInit: '#,##0',
  thousands1Decimal: '#,##0.0',
  percent2Decimal: '0.00%',
  percent1Decimal: '0.0%',
  bmk: '#,##0',
};

export const dimensionValueFormatRuleToExcelFormat = (
  rule?: string,
): string => {
  if (!rule || typeof rule !== 'string') {
    return '#,##0.00';
  }
  return DIMENSION_VALUE_FORMAT_RULE_TO_EXCEL[rule] ?? '#,##0.00';
};

/** 维度折线：日期 + 各维度列 + 事件/上周同期列（与 seriesData 字段对齐） */
export const buildDimensionTrendDownloadColumns = (
  dimensionSeriesConfig: SeriesConfig[],
  options: {
    isEN: boolean;
    valueFormatRule?: string;
    /** 默认 true；UA-ROI Spend 等无「上周同期」折线时不导出上周列 */
    includeLastWeekColumns?: boolean;
  },
): Array<{
  code: string;
  name: string;
  format?: string;
  type?: string;
  dependKey?: string;
  dateKey?: string;
  wrapText?: boolean;
}> => {
  const excelNum = dimensionValueFormatRuleToExcelFormat(
    options.valueFormatRule,
  );
  const includeLastWeek = options.includeLastWeekColumns !== false;
  const cols: Array<{
    code: string;
    name: string;
    format?: string;
    type?: string;
    dependKey?: string;
    dateKey?: string;
    wrapText?: boolean;
  }> = [];

  cols.push({
    code: 'date',
    name: getName(options.isEN, 'Date', '日期'),
    format: 'yyyy-mm-dd',
    type: 'date',
  });

  (dimensionSeriesConfig || []).forEach(s => {
    cols.push({
      code: s.metricKey,
      name: getName(options.isEN, s.enName, s.cnName) || s.metricKey,
      format: excelNum,
    });
  });

  cols.push({
    code: 'event',
    dependKey: 'date',
    dateKey: 'start_time',
    name: getName(options.isEN, 'Start Event Name (Today)', '当天开始事件名称'),
    format: '@',
    type: 'event',
    wrapText: true,
  });

  if (includeLastWeek) {
    cols.push({
      code: 'last_week_date',
      name: getName(
        options.isEN,
        'Date (same day last week)',
        '上周同期日期',
      ),
      format: 'yyyy-mm-dd',
      type: 'date',
    });

    cols.push({
      code: 'last_week_version_event',
      dependKey: 'last_week_date',
      dateKey: 'start_time',
      name: getName(
        options.isEN,
        "Start Event Name (Same Day Last Week)",
        '上周同期开始事件名称',
      ),
      format: '@',
      type: 'event',
      wrapText: true,
    });
  }

  return cols;
};

/**
 * 将已排序的 top 维度列表按 `priority` 重排：每个 token 依次匹配
 * 全等 `dim === token` 或组合键前缀 `dim.startsWith(token + '_')`（如 `1` 匹配 `1`、`1_us`），
 * 匹配项按原 `topDims` 相对顺序接在结果前部；未匹配的维度保持原顺序接在后面。
 */
function applyDimensionSeriesOrderPriority(
  topDims: string[],
  priority?: string[],
): string[] {
  if (
    !Array.isArray(priority) ||
    priority.length === 0 ||
    topDims.length === 0
  ) {
    return topDims;
  }
  const seen = new Set<string>();
  const front: string[] = [];

  const matchesToken = (token: string, dim: string): boolean => {
    if (dim === token) {
      return true;
    }
    return dim.startsWith(`${token}_`);
  };

  for (const raw of priority) {
    const token = String(raw ?? '').trim();
    if (!token) {
      continue;
    }
    for (const d of topDims) {
      if (seen.has(d)) {
        continue;
      }
      if (matchesToken(token, d)) {
        seen.add(d);
        front.push(d);
      }
    }
  }

  const rest = topDims.filter(d => !seen.has(d));
  return [...front, ...rest];
}

/**
 * dimension/metrics_trend：按维度值拆线，同日多行 → 每日期一行、每维度一列；并生成 dimensionSeriesConfig
 */
export const normalizeDimensionMetricsTrendResponse = (
  raw: unknown,
  requestedMetrics?: string[],
  options?: TNormalizeDimensionOptions,
): TDimensionNormalizedTrend => {
  const rows = extractDimensionTrendRows(raw);
  const mks = Array.isArray(requestedMetrics)
    ? requestedMetrics.filter(k => typeof k === 'string' && k)
    : [];
  const primary = mks[0];
  const dimensionKey = options?.dimensionKey ?? 'ua_network';
  const dimensionValueSource =
    options?.dimensionValueSource ?? 'custom_dimensions';
  const dimensionRowField = options?.dimensionRowField;
  const dimensionSecondaryRowField = options?.dimensionSecondaryRowField;
  const maxSeries = Math.max(
    1,
    Math.min(50, Number(options?.maxSeries ?? 10) || 10),
  );
  const valueFormatRule = options?.valueFormatRule ?? 'thousands2Decimal';
  const metricField =
    typeof options?.metricResponseKey === 'string' &&
    options.metricResponseKey.trim() !== ''
      ? options.metricResponseKey.trim()
      : primary;

  const empty: TDimensionNormalizedTrend = {
    xAxisData: [],
    seriesData: [],
    dimensionSeriesConfig: [],
    dimensionValueFormatRule: valueFormatRule,
  };

  if (!primary || rows.length === 0) {
    return empty;
  }

  const sumByDim = new Map<string, number>();
  rows.forEach(row => {
    const label = extractDimValue(
      row,
      dimensionKey,
      dimensionValueSource,
      dimensionRowField,
      dimensionSecondaryRowField,
    );
    const v = cellToOptionalNumber(row.metrics?.[metricField]);
    if (v === null) {
      return;
    }
    sumByDim.set(label, (sumByDim.get(label) ?? 0) + v);
  });

  const topDimsValueSorted = [...sumByDim.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxSeries)
    .map(([k]) => k);

  const topDims = applyDimensionSeriesOrderPriority(
    topDimsValueSorted,
    options?.dimensionSeriesOrderPriority,
  );

  if (topDims.length === 0) {
    return empty;
  }

  const byDateDim = new Map<string, Map<string, number>>();
  rows.forEach(row => {
    const d = normalizeDateKey(row.date);
    const label = extractDimValue(
      row,
      dimensionKey,
      dimensionValueSource,
      dimensionRowField,
      dimensionSecondaryRowField,
    );
    const v = cellToOptionalNumber(row.metrics?.[metricField]);
    if (!byDateDim.has(d)) {
      byDateDim.set(d, new Map());
    }
    if (v === null) {
      return;
    }
    const inner = byDateDim.get(d)!;
    inner.set(label, (inner.get(label) ?? 0) + v);
  });

  const sortedDates = [...byDateDim.keys()].sort();

  const colorMap = options?.dimensionSeriesColorMap;
  const resolveLineColor = (dimLabel: string, index: number): string => {
    if (colorMap && typeof colorMap === 'object' && !Array.isArray(colorMap)) {
      const key = String(dimLabel ?? '').trim();
      const hit =
        (key && colorMap[key]) ||
        (typeof dimLabel === 'string' && colorMap[dimLabel]);
      if (typeof hit === 'string' && hit.trim() !== '') {
        return hit.trim();
      }
    }
    return DIMENSION_LINE_COLORS[index % DIMENSION_LINE_COLORS.length];
  };

  const dimensionSeriesConfig: SeriesConfig[] = topDims.map((dimLabel, i) => {
    const names = toLegendNames(dimLabel);
    return {
      metricKey: `dim_v_${i}`,
      cnName: names.cnName,
      enName: names.enName,
      /** 接口维度原始值，用于 useSelectorMap / selectorMapKey 映射图例名 */
      dimRawCode: dimLabel,
      color: resolveLineColor(dimLabel, i),
      dateKey: 'date',
      formatRule: valueFormatRule as unknown as SeriesConfig['formatRule'],
    };
  });

  const seriesData = sortedDates.map(dateIso => {
    const dimMap = byDateDim.get(dateIso);
    const row: Record<string, unknown> = {
      date: dateIso,
      last_week_date: resolveLastWeekDateForTrendRow(dateIso, undefined),
      last_year_date: '',
      last_year_version_date: '',
      is_abnormal: false,
    };
    topDims.forEach((dimLabel, i) => {
      const mk = `dim_v_${i}`;
      let val: number | null = null;
      if (dimMap?.has(dimLabel)) {
        val = dimMap.get(dimLabel) as number;
      }
      row[mk] = val;
    });
    return shapeTrendSeriesRowLikeSpec(row);
  });

  return {
    xAxisData: sortedDates,
    seriesData,
    dimensionSeriesConfig,
    dimensionValueFormatRule: valueFormatRule,
  };
};

const DEFAULT_TREND_CHART_MULTI_SORT = [{ sort_by: 'date', desc: false }];

/**
 * 在 metrics_trend 入参基础上增加 dimension 接口字段：metric、logical_flag、top_num、accumulation
 *
 * `logical_flag === 'trend_chart'` 时与后端约定：start_date/end_date、metrics[]、multi_sort，
 * 不传 metric/top_num；accumulation 可与 by_country 组合（默认 by_country: false 可被配置覆盖）
 */
export const kpiDimensionMetricsTrendBeforeRequest = (
  data: IKpiDimensionReqData,
): Record<string, unknown> | false => {
  const base = kpiMetricsTrendBeforeRequest(data);
  if (base === false) {
    return false;
  }
  const metrics = Array.isArray(data?.metrics) ? data.metrics : [];
  const metric = metrics[0];
  if (!metric || typeof metric !== 'string') {
    return false;
  }

  const topNum = Number(data?.topNum ?? data?.config?.topNum ?? 10) || 10;
  const dimensionKey =
    data?.dimensionKey ?? data?.config?.dimensionKey ?? 'ua_network';
  const logicalFlag =
    data?.logicalFlag ?? data?.config?.logicalFlag ?? 'show_all_data';

  const accumulation =
    data?.accumulation ??
    data?.config?.accumulation ?? {
      custom_accumulation: [{ key: dimensionKey, is_accumulation: true }],
    };

  const { metrics: _drop, ...rest } = base as Record<string, unknown> & {
    metrics?: unknown;
  };

  if (logicalFlag === 'trend_chart') {
    const { start_time: st, end_time: et, ...restNoTime } = rest;
    if (!st || !et) {
      return false;
    }
    const multiSort =
      data?.multiSort ??
      data?.config?.multiSort ??
      DEFAULT_TREND_CHART_MULTI_SORT;
    const accFromConfig =
      accumulation && typeof accumulation === 'object' && !Array.isArray(accumulation)
        ? (accumulation as Record<string, unknown>)
        : {};
    const accumulationOut = {
      by_country: false,
      ...accFromConfig,
    };
    return {
      ...restNoTime,
      start_date: st,
      end_date: et,
      metrics,
      period: rest.period,
      logical_flag: 'trend_chart',
      accumulation: accumulationOut,
      multi_sort: multiSort,
    };
  }

  return {
    ...rest,
    metric,
    logical_flag: logicalFlag,
    top_num: topNum,
    accumulation,
  };
};

const FILTER_ALL_TOKEN = '255';

type TCustomFilterRow = { key: string; filter_list: string[]; };

function asNonEmptyStringArray(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  const out = raw.map(x => String(x).trim()).filter(Boolean);
  return out.length > 0 ? out : undefined;
}

/**
 * 筛选器 platform（system）与 allowedOsCodes 取交集写入 os_list；交集为空返回 null → 不发请求。
 * 选 255 或未选平台时展开为白名单中所有非 255 的值。
 */
export function resolveOsListForDimensionAllowlist(
  filterVal: Record<string, unknown> | undefined,
  allowedOsCodes: string[],
): string[] | null {
  const allowed = new Set(allowedOsCodes.map(String));
  const nonAllCodes = allowedOsCodes
    .map(String)
    .filter(c => c !== FILTER_ALL_TOKEN);
  const sys = filterVal?.system;
  let raw: string[];
  if (Array.isArray(sys)) {
    raw = sys.map(String);
  } else if (sys !== undefined && sys !== null) {
    raw = [String(sys)];
  } else {
    raw = [];
  }
  if (
    raw.length === 0 ||
    (raw.length === 1 && raw[0] === FILTER_ALL_TOKEN) ||
    raw.includes(FILTER_ALL_TOKEN)
  ) {
    return nonAllCodes.length > 0 ? nonAllCodes : null;
  }
  const intersection = raw.filter(o => allowed.has(String(o)));
  return intersection.length > 0 ? intersection : null;
}

/**
 * 筛选器 campaign 与 allowedCampaignCodes 取交集写入 custom_filters.campaign。
 * 仅选「全量」255 或未选子项时，请求侧使用完整白名单列表。
 */
export function resolveCampaignFilterListForAllowlist(
  filterVal: Record<string, unknown> | undefined,
  allowedCampaignCodes: string[],
): string[] | null {
  const allowed = new Set(allowedCampaignCodes.map(String));
  const selected = getCascadeChildValues(filterVal, 'campaign');
  const nonAll = selected.filter(s => String(s) !== FILTER_ALL_TOKEN);
  if (nonAll.length === 0) {
    const list = [...new Set(allowedCampaignCodes.map(String))];
    return list.length > 0 ? list : null;
  }
  const hit = nonAll.filter(c => allowed.has(String(c)));
  return hit.length > 0 ? hit : null;
}

/**
 * 筛选器 ua_network 与 allowedUaNetworkCodes 取交集写入 custom_filters.ua_network。
 * 仅选「全量」255 或未选子项时，请求侧使用完整白名单列表。
 */
export function resolveUaNetworkFilterListForAllowlist(
  filterVal: Record<string, unknown> | undefined,
  allowedUaNetworkCodes: string[],
): string[] | null {
  const allowed = new Set(allowedUaNetworkCodes.map(String));
  const selected = getCascadeChildValues(filterVal, 'ua_network');
  const nonAll = selected.filter(s => String(s) !== FILTER_ALL_TOKEN);
  if (nonAll.length === 0) {
    const list = [...new Set(allowedUaNetworkCodes.map(String))];
    return list.length > 0 ? list : null;
  }
  const hit = nonAll.filter(c => allowed.has(String(c)));
  return hit.length > 0 ? hit : null;
}

export function mergeDimensionAllowlistIntoFilters(
  filters: Record<string, unknown>,
  patch: { osList?: string[]; campaignList?: string[]; uaNetworkList?: string[]; },
): Record<string, unknown> {
  const next = { ...filters };
  if (patch.osList && patch.osList.length > 0) {
    next.os_list = patch.osList;
  }
  const cfKeys: Array<{ key: string; list: string[] | undefined; }> = [
    { key: 'campaign', list: patch.campaignList },
    { key: 'ua_network', list: patch.uaNetworkList },
  ];
  const hasCustom = cfKeys.some(k => k.list && k.list.length > 0);
  if (hasCustom) {
    const cf = Array.isArray(next.custom_filters)
      ? [...(next.custom_filters as TCustomFilterRow[])]
      : [];
    cfKeys.forEach(({ key, list }) => {
      if (!list || list.length === 0) return;
      const idx = cf.findIndex(r => r && r.key === key);
      const row: TCustomFilterRow = { key, filter_list: list };
      if (idx >= 0) {
        cf[idx] = row;
      } else {
        cf.push(row);
      }
    });
    next.custom_filters = cf;
  }
  return next;
}

/**
 * 在 dimension/metrics_trend 请求体上叠加 allowedOsCodes / allowedCampaignCodes（由 Service data 注入）。
 * buildBase 默认 kpiDimensionMetricsTrendBeforeRequest，可传入自定义 beforeRequest 的内核。
 */
export function kpiDimensionMetricsTrendBeforeRequestWithAllowlists(
  data: IKpiDimensionReqData,
  buildBase: (d: IKpiDimensionReqData) => Record<string, unknown> | false = kpiDimensionMetricsTrendBeforeRequest,
): Record<string, unknown> | false {
  const base = buildBase(data);
  if (base === false) {
    return false;
  }
  const allowedCampaign = asNonEmptyStringArray(
    data?.allowedCampaignCodes ?? data?.config?.allowedCampaignCodes,
  );
  const allowedOs = asNonEmptyStringArray(
    data?.allowedOsCodes ?? data?.config?.allowedOsCodes,
  );
  const allowedUaNetwork = asNonEmptyStringArray(
    data?.allowedUaNetworkCodes ?? data?.config?.allowedUaNetworkCodes,
  );
  if (!allowedCampaign && !allowedOs && !allowedUaNetwork) {
    return base;
  }
  const fv = data?.filterVal as Record<string, unknown> | undefined;
  const next: Record<string, unknown> = { ...base };
  const filters =
    next.filters && typeof next.filters === 'object' && !Array.isArray(next.filters)
      ? { ...(next.filters as Record<string, unknown>) }
      : {};

  let merged: Record<string, unknown> = { ...filters };
  if (allowedOs) {
    const osList = resolveOsListForDimensionAllowlist(fv, allowedOs);
    if (osList === null) {
      return false;
    }
    merged = mergeDimensionAllowlistIntoFilters(merged, { osList });
  }
  if (allowedCampaign) {
    const campaignList = resolveCampaignFilterListForAllowlist(fv, allowedCampaign);
    if (campaignList === null) {
      return false;
    }
    merged = mergeDimensionAllowlistIntoFilters(merged, { campaignList });
  }
  if (allowedUaNetwork) {
    const uaNetworkList = resolveUaNetworkFilterListForAllowlist(fv, allowedUaNetwork);
    if (uaNetworkList === null) {
      return false;
    }
    merged = mergeDimensionAllowlistIntoFilters(merged, { uaNetworkList });
  }
  next.filters = merged;
  return next;
}
